import { Alert } from '@mui/material';
import HubSignIn from 'components/account/hub-sign-in';
import { Button, Input, Link } from 'components/elements';
import logoSrc from 'components/navbar/assets/logo.png';
import Toast from 'components/toast';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import useForm from 'react-hook-form';
import AuthService from 'services/auth-service';
import {
  endCoreSession,
  isHubSignInEnabled,
  isHubSignInErrorCode,
} from 'services/hub-auth-service';
import { sessionMonitor } from 'services/session';
import { trackSignIn } from 'services/sign-in-tracking';
import * as yup from 'yup';
import {
  AccountContent,
  AccountHeader,
  AccountWrapper,
  Background,
  Form,
  Logo,
  Subtitle,
} from '../account-styles';

const schema = yup.object().shape({
  phone: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required field'),
  password: yup.string().required('Password is a required field'),
});

const SESSION_ENDED_NOTICES = {
  expired: { severity: 'warning', message: 'Your session has expired. Please sign in again.' },
  'other-tab': { severity: 'info', message: 'You were signed out in another tab.' },
};

const sessionEndedReason = (reason) =>
  reason === 'expired' || reason === 'other-tab' ? reason : null;

const Login = ({ history, location }) => {
  const { handleSubmit, register, errors, formState } = useForm({
    validationSchema: schema,
  });
  const [hubSignInError] = useState(() =>
    isHubSignInErrorCode(location.state?.hubSignInError) ? location.state.hubSignInError : null
  );
  // A failed Hub sign-in says more than how the previous session ended.
  const [sessionEnded] = useState(() =>
    hubSignInError ? null : sessionEndedReason(location.state?.sessionEnded)
  );

  // Drop the notice from history, so a reload does not show it again.
  useEffect(() => {
    if (location.state) {
      history.replace(location.pathname);
    }
  }, []);

  const submit = async (payload) => {
    try {
      const response = await AuthService.signIn(payload);
      const { data } = response;

      const previousHubToken = AuthService.getHubToken();
      // Written first: if storage refuses it, the session stored before is untouched.
      AuthService.setAuth(data);
      // The previous session's deadline and Hub token are not this session's.
      AuthService.clearSessionMarkers();
      sessionMonitor.reset();
      // A previous Hub session is ended in Core only (S1), as a Hub sign-in does.
      if (previousHubToken && previousHubToken !== data.auth_token) {
        endCoreSession(previousHubToken);
      }
      trackSignIn({ uuid: data.user_uuid, userId: data.user_id, roles: data.roles });

      return history.push('/');
    } catch (err) {
      Toast.show('error', 'Your username or password doesn’t exist.');
    }
  };

  return (
    <AccountWrapper>
      <Background />

      <AccountHeader>
        <Logo src={logoSrc} alt='Enara logo' />
      </AccountHeader>

      <AccountContent>
        <Subtitle>We are glad to have you back</Subtitle>
        {sessionEnded && (
          <Alert
            severity={SESSION_ENDED_NOTICES[sessionEnded].severity}
            sx={{ maxWidth: 390, mx: 'auto', mb: 3, textAlign: 'left' }}>
            {SESSION_ENDED_NOTICES[sessionEnded].message}
          </Alert>
        )}
        {isHubSignInEnabled() && <HubSignIn error={hubSignInError} />}
        <Form onSubmit={handleSubmit(submit)}>
          <Input errors={errors.phone} label='Email' name='phone' register={register} type='text' />

          <Input
            errors={errors.password}
            label='Password'
            name='password'
            register={register}
            type='password'
          />

          <Button type='submit' size='large' submitting={formState.isSubmitting}>
            Next
          </Button>
        </Form>

        <Link to='/forgot-password' title='Forgot password'>
          I forgot my password
        </Link>
      </AccountContent>
    </AccountWrapper>
  );
};

Login.propTypes = {
  history: PropTypes.shape().isRequired,
  // `state` carries `sessionEnded` or `hubSignInError` (see LoginLocationState
  // in services/session).
  location: PropTypes.shape({ pathname: PropTypes.string }).isRequired,
};

export default Login;
